import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getFullDayLockedDates, getListingByID, getListingLockDatesByID } from './utils';
import { Container, Row, Col, Button, Alert, Badge, Form, OverlayTrigger, Popover, Dropdown } from 'react-bootstrap';
import PackageCard from './PackageCard';
import PackageInstantBookModal from './PackageInstantBookModal';
import { Slider, Typography, Skeleton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const ListingPackages = () => {
    const { dynamicId } = useParams();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [disabledDates, setDisabledDates] = useState([]);
    const [lockedDates, setLockedDates] = useState({});

    // Instant booking states
    const { currentUser } = useAuth();
    const [isPackageInstantBookModalOpen, setIsPackageInstantBookModalOpen] = useState(false);
    const [selectedPackageForInstantBook, setSelectedPackageForInstantBook] = useState(null);

    // Filters and Sort state
    const [activeFilter, setActiveFilter] = useState({
        price: [],
        guests: 1, // Specific passenger count
        duration: [],
        date: null // Selected date
    });
    const [sortOrder, setSortOrder] = useState("price_asc");
    const [tempSortOrder, setTempSortOrder] = useState("price_asc");
    const [showSortPopover, setShowSortPopover] = useState(false);
    const [showDatePopover, setShowDatePopover] = useState(false);
    const [showGuestsPopover, setShowGuestsPopover] = useState(false);

    const [tempFilters, setTempFilters] = useState({
        price: [0, 5000],
        guests: 1,
        duration: [],
        date: null
    });

    // Derived state for slider bounds
    const priceBounds = useMemo(() => {
        if (!listing?.fishingGuideDetails?.tripPackages) return { min: 0, max: 5000 };
        const prices = listing.fishingGuideDetails.tripPackages.map(p => parseInt(p.basePrice) || 0);
        return {
            min: Math.min(...prices, 0),
            max: Math.max(...prices, 1000) // Ensure at least some range
        };
    }, [listing]);

    // Update defaults when listing loads (optional, but good for UX)
    useEffect(() => {
        if (priceBounds.max !== 5000 && activeFilter.price.length === 0) {
            // Set default range to full range if not set
            setTempFilters(prev => ({ ...prev, price: [priceBounds.min, priceBounds.max] }));
            setActiveFilter(prev => ({ ...prev, price: [priceBounds.min, priceBounds.max] }));
        }
    }, [priceBounds, activeFilter.price.length]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const fetchListingDetails = async () => {
            const listingID = String(dynamicId);
            try {
                const details = await getListingByID(listingID);
                setListing(details);

                // Fetch lock dates
                const lockDates = await getListingLockDatesByID(listingID);
                if (lockDates) {
                    setLockedDates(lockDates);
                    setDisabledDates(getFullDayLockedDates(lockDates, 'YYYY-MM-DD'));
                } else {
                    setLockedDates({});
                    setDisabledDates([]);
                }
            } catch (error) {
                console.error("Error fetching listing details:", error);
                setError("Failed to load listing details.");
            } finally {
                setLoading(false);
            }
        };
        fetchListingDetails();
    }, [dynamicId]);

    const openPackageInstantBookModal = (tripPackage) => {
        setSelectedPackageForInstantBook(tripPackage);
        setIsPackageInstantBookModalOpen(true);
    };

    const closePackageInstantBookModal = () => {
        setIsPackageInstantBookModalOpen(false);
        setSelectedPackageForInstantBook(null);
    };

    // Filter Logic
    const filteredPackages = useMemo(() => {
        if (!listing?.fishingGuideDetails?.tripPackages) return [];

        let packages = listing.fishingGuideDetails.tripPackages.filter(pkg => pkg.instantBook); // Show only instant book packages

        // Filter: guests - show packages that can accommodate the selected passenger count
        if (activeFilter.guests > 0) {
            packages = packages.filter(pkg => {
                const included = parseInt(pkg.maxGuests) || 1;
                const additional = parseInt(pkg.priceChangeOptions?.afterGuests) || 0;
                const totalMax = included + additional;
                return activeFilter.guests <= totalMax;
            });
        }

        // Filter: date
        if (activeFilter.date) {
            const selectedDate = dayjs(activeFilter.date);
            const formattedDate = selectedDate.format("YYYY-MM-DD");

            packages = packages.filter(pkg => {
                // 1. Check disabled dates (locked)
                if (disabledDates.includes(formattedDate)) return false;

                // 2. Check package available date range
                if (pkg.availableDateRange?.startDate && pkg.availableDateRange?.endDate) {
                    const startDate = dayjs(pkg.availableDateRange.startDate);
                    const endDate = dayjs(pkg.availableDateRange.endDate);
                    if (selectedDate.isBefore(startDate) || selectedDate.isAfter(endDate)) {
                        return false;
                    }
                }

                // 3. Check days offered
                if (pkg.daysOffered) {
                    const dayName = selectedDate.format('ddd');
                    if (!pkg.daysOffered[dayName]) {
                        return false;
                    }
                }

                return true;
            });
        }

        // Filter: duration (heuristic parsing)
        if (activeFilter.duration.length > 0) {
            packages = packages.filter(pkg => {
                const dur = parseInt(pkg.duration) || 0; // "4 hours" -> 4
                return activeFilter.duration.some(range => {
                    if (range === 'half') return dur <= 5;
                    if (range === 'full') return dur >= 6;
                    return false;
                });
            });
        }

        // Filter: price
        if (activeFilter.price.length === 2) {
            packages = packages.filter(pkg => {
                const price = parseInt(pkg.basePrice) || 0;
                return price >= activeFilter.price[0] && price <= activeFilter.price[1];
            });
        }

        // Sort
        if (sortOrder === "price_asc") {
            packages.sort((a, b) => parseInt(a.basePrice) - parseInt(b.basePrice));
        } else if (sortOrder === "price_desc") {
            packages.sort((a, b) => parseInt(b.basePrice) - parseInt(a.basePrice));
        } else if (sortOrder === "duration_asc") {
            // Basic duration sort assuming format "X hours"
            packages.sort((a, b) => parseInt(a.duration) - parseInt(b.duration));
        } else if (sortOrder === "duration_desc") {
            packages.sort((a, b) => parseInt(b.duration) - parseInt(a.duration));
        }

        return packages;
    }, [listing, activeFilter, sortOrder]);


    if (loading) {
        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={8}>
                        {/* Back button skeleton */}
                        <Skeleton variant="rounded" width={100} height={40} className="mb-4" sx={{ bgcolor: 'grey.800' }} />

                        {/* Title skeleton */}
                        <Skeleton variant="text" width="60%" height={40} className="mb-2" sx={{ bgcolor: 'grey.800' }} />
                        <Skeleton variant="text" width="40%" height={20} className="mb-4" sx={{ bgcolor: 'grey.800' }} />

                        {/* Filters skeleton */}
                        <div className="d-flex gap-2 mb-4">
                            <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius: '20px', bgcolor: 'grey.800' }} />
                            <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: '20px', bgcolor: 'grey.800' }} />
                            <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: '20px', bgcolor: 'grey.800' }} />
                            <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: '20px', bgcolor: 'grey.800' }} />
                        </div>

                        {/* Package Cards skeleton */}
                        {[1, 2, 3].map((item) => (
                            <div key={item} className="mb-3 p-3 rounded" style={{ backgroundColor: '#1c1c1c' }}>
                                <Row>
                                    <Col xs={12} md={8}>
                                        <Skeleton variant="text" width="50%" height={30} className="mb-2" sx={{ bgcolor: 'grey.800' }} />
                                        <div className="d-flex gap-4 mb-3">
                                            <Skeleton variant="text" width={60} height={20} sx={{ bgcolor: 'grey.800' }} />
                                            <Skeleton variant="text" width={60} height={20} sx={{ bgcolor: 'grey.800' }} />
                                            <Skeleton variant="text" width={60} height={20} sx={{ bgcolor: 'grey.800' }} />
                                        </div>
                                        <Skeleton variant="rectangular" width="100%" height={60} sx={{ bgcolor: 'grey.800' }} />
                                    </Col>
                                    <Col xs={12} md={4} className="d-flex flex-column justify-content-center align-items-end mt-3 mt-md-0">
                                        <Skeleton variant="rectangular" width="100%" height={40} className="mb-2" sx={{ bgcolor: 'grey.800' }} />
                                        <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'grey.800' }} />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </Col>
                </Row>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
                <Link to={`/listing/${dynamicId}`}>
                    <Button variant="secondary">Back to Listing</Button>
                </Link>
            </Container>
        );
    }

    const FilterButton = ({ label, value, type = "filter" }) => {
        const isActive = type === 'filter' ? activeFilter === value : sortOrder === value;
        return (
            <Button
                variant={isActive ? "primary" : "outline-secondary"}
                className="rounded-pill me-2 mb-2"
                size="sm"
                style={{
                    borderRadius: '20px',
                    border: '1px solid #dee2e6',
                    color: isActive ? '#fff' : '#495057',
                    backgroundColor: isActive ? '#0d6efd' : '#fff'
                }}
                onClick={() => {
                    if (type === 'filter') {
                        setActiveFilter(activeFilter === value ? "" : value);
                    } else {
                        setSortOrder(sortOrder === value ? "" : value);
                    }
                }}
            >
                {label}
            </Button>
        );
    };

    const getSortLabel = (sort) => {
        switch (sort) {
            case 'price_asc': return "Price (Lowest)";
            case 'price_desc': return "Price (Highest)";
            case 'duration_asc': return "Duration (Shortest)";
            case 'duration_desc': return "Duration (Longest)";
            default: return "Price (Lowest)";
        }
    };

    const sortPopover = (
        <Popover id="sort-popover" style={{ minWidth: '200px', color: 'black' }}>
            <Popover.Body>
                <Form>
                    {[
                        { label: "Price (Lowest)", value: "price_asc" },
                        { label: "Price (Highest)", value: "price_desc" },
                        { label: "Duration (Shortest)", value: "duration_asc" },
                        { label: "Duration (Longest)", value: "duration_desc" },
                    ].map((option) => (
                        <Form.Check
                            type="radio"
                            id={`sort-${option.value}`}
                            key={option.value}
                            label={option.label}
                            name="sortOrder"
                            value={option.value}
                            checked={tempSortOrder === option.value}
                            onChange={(e) => setTempSortOrder(e.target.value)}
                            className="mb-2"
                        />
                    ))}
                    <Button
                        className="w-100 mt-2"
                        variant="primary"
                        onClick={() => {
                            setSortOrder(tempSortOrder);
                            document.body.click(); // Close popover by clicking body
                        }}
                    >
                        Apply
                    </Button>
                </Form>
            </Popover.Body>
        </Popover>
    );

    const FilterPopover = ({ title, type, options }) => (
        <Popover id={`filter-${type}-popover`} style={{ minWidth: '200px', color: 'black' }}>
            <Popover.Body>
                <Form>
                    {options.map((option) => (
                        <Form.Check
                            type="checkbox"
                            id={`filter-${type}-${option.value}`}
                            key={option.value}
                            label={option.label}
                            checked={tempFilters[type].includes(option.value)}
                            onChange={(e) => {
                                const val = option.value;
                                const checked = e.target.checked;
                                setTempFilters(prev => ({
                                    ...prev,
                                    [type]: checked
                                        ? [...prev[type], val]
                                        : prev[type].filter(item => item !== val)
                                }));
                            }}
                            className="mb-2"
                        />
                    ))}
                    <div className="d-flex gap-2 mt-2">
                        <Button
                            variant="primary"
                            size="sm"
                            className="w-100"
                            onClick={() => {
                                setActiveFilter(prev => ({ ...prev, [type]: tempFilters[type] }));
                                document.body.click();
                            }}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="w-100"
                            onClick={() => {
                                setTempFilters(prev => ({ ...prev, [type]: [] }));
                                setActiveFilter(prev => ({ ...prev, [type]: [] }));
                                document.body.click();
                            }}
                        >
                            Reset
                        </Button>
                    </div>
                </Form>
            </Popover.Body>
        </Popover>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Container className="py-5" style={{ color: 'white' }}>
                <Row className="justify-content-center">
                    <Col md={8}>
                        <Row className="mb-4">
                            <Col>
                                <Link to={`/listing/${dynamicId}`}>
                                    <Button variant="outline-light">
                                        <i className="material-icons align-middle me-1">arrow_back</i>
                                        Back to Listing
                                    </Button>
                                </Link>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <h2>Available Packages for {listing.listingTitle}</h2>
                                <p className="text-white-50">Select a package to book instantly.</p>
                            </Col>
                        </Row>

                        {/* Filters */}
                        <Row className="mb-4">
                            <Col className="d-flex flex-wrap gap-2 align-items-center">
                                <Dropdown
                                    autoClose="outside"
                                    show={showSortPopover} // We can control manually or let it handle itself, but let's try standard first or hybrid. Actually standard 'onToggle' is enough usually.
                                    onToggle={(isOpen) => {
                                        setShowSortPopover(isOpen);
                                        if (isOpen) setTempSortOrder(sortOrder);
                                    }}
                                >
                                    <Dropdown.Toggle
                                        variant="outline-light"
                                        id="dropdown-sort"
                                        className="rounded-pill me-3 d-flex align-items-center mb-2 no-caret-toggle"
                                        style={{ borderRadius: '20px' }}
                                    >
                                        <i className="material-icons me-1" style={{ fontSize: '1.2rem' }}>swap_vert</i>
                                        Sort by {getSortLabel(sortOrder)}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="p-3" style={{ minWidth: '250px' }}>
                                        <Form>
                                            {[
                                                { label: "Price (Lowest)", value: "price_asc" },
                                                { label: "Price (Highest)", value: "price_desc" },
                                                { label: "Duration (Shortest)", value: "duration_asc" },
                                                { label: "Duration (Longest)", value: "duration_desc" },
                                            ].map((option) => (
                                                <Form.Check
                                                    type="radio"
                                                    id={`sort-${option.value}`}
                                                    key={option.value}
                                                    label={option.label}
                                                    name="sortOrder"
                                                    value={option.value}
                                                    checked={tempSortOrder === option.value}
                                                    onChange={(e) => setTempSortOrder(e.target.value)}
                                                    className="mb-2"
                                                />
                                            ))}
                                            <Button
                                                className="w-100 mt-2"
                                                variant="primary"
                                                onClick={() => {
                                                    setSortOrder(tempSortOrder);
                                                    setShowSortPopover(false);
                                                    document.body.click(); // Ensure close
                                                }}
                                            >
                                                Apply
                                            </Button>
                                        </Form>
                                    </Dropdown.Menu>
                                </Dropdown>

                                <div className="vr mx-2 text-white-50 mb-2"></div>

                                {/* Date Filter */}
                                <Dropdown
                                    autoClose={false}
                                    show={showDatePopover}
                                    onToggle={(isOpen) => {
                                        setShowDatePopover(isOpen);
                                        if (isOpen) setTempFilters(prev => ({ ...prev, date: activeFilter.date }));
                                    }}
                                >
                                    <Dropdown.Toggle
                                        variant="outline-light"
                                        className={`rounded-pill me-2 d-flex align-items-center mb-2 no-caret-toggle ${activeFilter.date ? "active" : ""}`}
                                        style={{ borderRadius: '20px' }}
                                    >
                                        <i className="material-icons me-1" style={{ fontSize: '1.2rem' }}>calendar_today</i>
                                        {activeFilter.date ? dayjs(activeFilter.date).format('MMM D, YYYY') : "Date"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-3" style={{ minWidth: "300px" }}>
                                        <div className="px-2">
                                            <DatePicker
                                                label="Select Date"
                                                value={tempFilters.date ? dayjs(tempFilters.date) : null}
                                                onChange={(date) => setTempFilters(prev => ({ ...prev, date: date }))}
                                                minDate={dayjs()}
                                                shouldDisableDate={(date) => {
                                                    const formattedDate = dayjs(date).format("YYYY-MM-DD");
                                                    return disabledDates.includes(formattedDate);
                                                }}
                                                slotProps={{ textField: { fullWidth: true } }}
                                            />
                                            <div className="d-flex gap-2 mt-3">
                                                <Button variant="primary" size="sm" className="w-100" onClick={() => {
                                                    setActiveFilter(prev => ({ ...prev, date: tempFilters.date }));
                                                    setShowDatePopover(false);
                                                }}>Apply</Button>
                                                <Button variant="outline-secondary" size="sm" className="w-100" onClick={() => {
                                                    setTempFilters(prev => ({ ...prev, date: null }));
                                                    setActiveFilter(prev => ({ ...prev, date: null }));
                                                    setShowDatePopover(false);
                                                }}>Reset</Button>
                                            </div>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>

                                {/* Passengers Filter */}
                                <Dropdown
                                    autoClose={false}
                                    show={showGuestsPopover}
                                    onToggle={(isOpen) => {
                                        setShowGuestsPopover(isOpen);
                                        if (isOpen) setTempFilters(prev => ({ ...prev, guests: activeFilter.guests }));
                                    }}
                                >
                                    <Dropdown.Toggle
                                        variant="outline-light"
                                        className={`rounded-pill me-2 d-flex align-items-center mb-2 no-caret-toggle ${activeFilter.guests > 1 ? "active" : ""}`}
                                        style={{ borderRadius: '20px' }}
                                    >
                                        <i className="material-icons me-1" style={{ fontSize: '1.2rem' }}>group</i>
                                        {activeFilter.guests} Passenger{activeFilter.guests > 1 ? 's' : ''}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-3" style={{ minWidth: "200px" }}>
                                        <div className="text-center">
                                            <h6 className="mb-3" style={{ color: 'black' }}>Number of Passengers</h6>
                                            <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    style={{ width: '36px', height: '36px', borderRadius: '50%', padding: 0, fontSize: '1.2rem', lineHeight: 1 }}
                                                    onClick={() => setTempFilters(prev => ({ ...prev, guests: Math.max(1, prev.guests - 1) }))}
                                                    disabled={tempFilters.guests <= 1}
                                                >
                                                    −
                                                </Button>
                                                <span style={{ fontSize: '1.2rem', fontWeight: 600, minWidth: '40px', color: 'black' }}>
                                                    {tempFilters.guests}
                                                </span>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    style={{ width: '36px', height: '36px', borderRadius: '50%', padding: 0, fontSize: '1.2rem', lineHeight: 1 }}
                                                    onClick={() => setTempFilters(prev => ({ ...prev, guests: prev.guests + 1 }))}
                                                >
                                                    +
                                                </Button>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button variant="primary" size="sm" className="w-100" onClick={() => {
                                                    setActiveFilter(prev => ({ ...prev, guests: tempFilters.guests }));
                                                    setShowGuestsPopover(false);
                                                }}>Apply</Button>
                                                <Button variant="outline-secondary" size="sm" className="w-100" onClick={() => {
                                                    setTempFilters(prev => ({ ...prev, guests: 1 }));
                                                    setActiveFilter(prev => ({ ...prev, guests: 1 }));
                                                    setShowGuestsPopover(false);
                                                }}>Reset</Button>
                                            </div>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>

                                {/* Price Filter */}
                                <Dropdown
                                    autoClose="outside"
                                    onToggle={(isOpen) => {
                                        if (isOpen) setTempFilters(prev => ({ ...prev, price: activeFilter.price.length ? activeFilter.price : [priceBounds.min, priceBounds.max] }));
                                    }}
                                >
                                    <Dropdown.Toggle
                                        variant="outline-light"
                                        className={`rounded-pill me-2 d-flex align-items-center mb-2 no-caret-toggle ${activeFilter.price.length === 2 && (activeFilter.price[0] !== priceBounds.min || activeFilter.price[1] !== priceBounds.max) ? "active" : ""}`}
                                        style={{ borderRadius: '20px' }}
                                    >
                                        <i className="material-icons me-1" style={{ fontSize: '1.2rem' }}>attach_money</i>
                                        Price {activeFilter.price.length === 2 && (activeFilter.price[0] !== priceBounds.min || activeFilter.price[1] !== priceBounds.max) && `($${activeFilter.price[0]} - $${activeFilter.price[1]})`}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-3" style={{ minWidth: "300px" }}>
                                        <div className="px-2">
                                            <Typography gutterBottom>
                                                Price Range: ${tempFilters.price[0]} - ${tempFilters.price[1]}
                                            </Typography>
                                            <Slider
                                                value={tempFilters.price}
                                                onChange={(e, newValue) => setTempFilters(prev => ({ ...prev, price: newValue }))}
                                                valueLabelDisplay="auto"
                                                min={priceBounds.min}
                                                max={priceBounds.max}
                                                step={10}
                                            />
                                            <div className="d-flex gap-2 mt-3">
                                                <Button variant="primary" size="sm" className="w-100" onClick={() => { setActiveFilter(prev => ({ ...prev, price: tempFilters.price })); document.body.click(); }}>Apply</Button>
                                                <Button variant="outline-secondary" size="sm" className="w-100" onClick={() => {
                                                    const defaultRange = [priceBounds.min, priceBounds.max];
                                                    setTempFilters(prev => ({ ...prev, price: defaultRange }));
                                                    setActiveFilter(prev => ({ ...prev, price: defaultRange }));
                                                    document.body.click();
                                                }}>Reset</Button>
                                            </div>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>

                                {/* Duration Filter */}
                                <Dropdown
                                    autoClose="outside"
                                    onToggle={(isOpen) => {
                                        if (isOpen) setTempFilters(prev => ({ ...prev, duration: activeFilter.duration }));
                                    }}
                                >
                                    <Dropdown.Toggle
                                        variant="outline-light"
                                        className={`rounded-pill me-2 d-flex align-items-center mb-2 no-caret-toggle ${activeFilter.duration.length > 0 ? "active" : ""}`}
                                        style={{ borderRadius: '20px' }}
                                    >
                                        <i className="material-icons me-1" style={{ fontSize: '1.2rem' }}>schedule</i>
                                        Duration {activeFilter.duration.length > 0 && `(${activeFilter.duration.length})`}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-3" style={{ minWidth: "200px" }}>
                                        <Form>
                                            {[
                                                { label: "Half Day (Up to 5h)", value: "half" },
                                                { label: "Full Day (6h+)", value: "full" }
                                            ].map((option) => (
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`filter-duration-${option.value}`}
                                                    key={option.value}
                                                    label={option.label}
                                                    checked={tempFilters.duration.includes(option.value)}
                                                    onChange={(e) => {
                                                        const val = option.value;
                                                        const checked = e.target.checked;
                                                        setTempFilters(prev => ({
                                                            ...prev,
                                                            duration: checked
                                                                ? [...prev.duration, val]
                                                                : prev.duration.filter(item => item !== val)
                                                        }));
                                                    }}
                                                    className="mb-2"
                                                />
                                            ))}
                                            <div className="d-flex gap-2 mt-2">
                                                <Button variant="primary" size="sm" className="w-100" onClick={() => { setActiveFilter(prev => ({ ...prev, duration: tempFilters.duration })); document.body.click(); }}>Apply</Button>
                                                <Button variant="outline-secondary" size="sm" className="w-100" onClick={() => { setTempFilters(prev => ({ ...prev, duration: [] })); setActiveFilter(prev => ({ ...prev, duration: [] })); document.body.click(); }}>Reset</Button>
                                            </div>
                                        </Form>
                                    </Dropdown.Menu>
                                </Dropdown>

                                {(activeFilter.guests > 1 || activeFilter.date || activeFilter.price.length > 0 || activeFilter.duration.length > 0) && (
                                    <Button
                                        variant="link"
                                        className="text-white mb-2"
                                        onClick={() => setActiveFilter({ price: [], guests: 1, duration: [], date: null })}
                                    >
                                        Clear all
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {filteredPackages.length > 0 ? (
                            filteredPackages.map((tripPackage, index) => (
                                <PackageCard
                                    key={index}
                                    tripPackage={tripPackage}
                                    onInstantBook={openPackageInstantBookModal}
                                />
                            ))
                        ) : (
                            <div className="text-white text-center py-5">
                                <h4>No packages match your criteria.</h4>
                                <p className="text-white-50">Try adjusting your filters or sorting.</p>
                            </div>
                        )}

                        {/* Package Instant Book Modal */}
                        <PackageInstantBookModal
                            open={isPackageInstantBookModalOpen}
                            onClose={closePackageInstantBookModal}
                            selectedPackage={selectedPackageForInstantBook}
                            listing={listing}
                            currentUser={currentUser}
                            disabledDates={disabledDates}
                            lockedDates={lockedDates}
                        />
                    </Col>
                </Row>
            </Container>
        </LocalizationProvider>
    );
};

export default ListingPackages;
